import { useEffect, useRef } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';

export const DietGlobalWorker = () => {
    const { user, subscriptionStatus } = useAuth();
    const isPremium = subscriptionStatus === 'active' || subscriptionStatus === 'trialing' || subscriptionStatus === 'pro' || localStorage.getItem('aura_demo_mode') === 'true';

    // To prevent duplicate fallback executions
    const isExecutingFallbackRef = useRef(false);

    useEffect(() => {
        if (!user) return;

        const handleJob = async (profileData: any) => {
            if (!isPremium) return;
            if (!profileData) return;

            let statsObj = profileData.stats || {};
            if (typeof statsObj === 'string') {
                try { statsObj = JSON.parse(statsObj); } catch(e) { }
            }

            const status = profileData.aura_diet_status;
            if (!status) return;

            // Direct frontend invocation to bypass pg_net 5s limit
            if (status === 'generating_initial' || status === 'trigger_update') {
                const pStart = profileData.aura_diet_process_start || statsObj.aura_diet_process_start;
                const processId = pStart ? String(pStart) : 'unknown';
                const currentActiveProcess = localStorage.getItem('aura_diet_generating_active');
                
                if (currentActiveProcess !== processId) {
                    localStorage.setItem('aura_diet_generating_active', processId);
                    console.log(`[GlobalWorker] Invoking generate-diet Edge Function for process: ${processId}`);
                    
                    supabase.functions.invoke('generate-diet', {
                        body: {
                            record: { 
                                ...profileData, 
                                aura_diet_status: status,
                                subscription_status: profileData.subscription_status || 'active'
                            },
                            old_record: { aura_diet_status: 'idle' }
                        },
                        headers: {
                            'x-manual-invoke': 'true'
                        }
                    }).catch(err => {
                        console.warn("[GlobalWorker] Direct invocation warning (expected on long tasks):", err);
                    });
                }
            }

            // Cloud Diet Completion Detection
            const prevStatus = sessionStorage.getItem('aura_diet_prev_status');
            const wasProcessing = prevStatus === 'processing' || prevStatus === 'updating' || prevStatus === 'trigger_update' || prevStatus === 'generating_initial';
            
            if (status === 'completed') {
                const hasPendingReveal = statsObj.needs_diet_reveal === true;
                
                // If we transition from processing to completed OR the backend left the target time, it's a fresh completion
                const cloudTarget = profileData.aura_target_completion_time || statsObj.aura_target_completion_time;
                if ((cloudTarget || wasProcessing) && !hasPendingReveal) {
                    console.log(`[GlobalWorker] Detected fresh Cloud Diet Completion. Setting needs_diet_reveal to true.`);
                    
                    try {
                        const { error } = await supabase.from('profiles').update({
                            stats: {
                                ...statsObj,
                                needs_diet_reveal: true
                            }
                        }).eq('id', user.id);
                        
                        if (!error) {
                            window.dispatchEvent(new Event('aura_diet_updated'));
                        }
                    } catch (e) {
                        console.warn("Fatal Error in Global Worker Update", e);
                    }
                }
            }

            // V2205: CENTRALIZED INVISIBLE FALLBACK
            // If the backend throws a hard error OR the timer expires, we silently rescue the user.
            const executeFallback = async () => {
                if (isExecutingFallbackRef.current) return;
                isExecutingFallbackRef.current = true;
                console.warn("[GlobalWorker] Triggering silent emergency fallback completion...");
                
                try {
                    let currentStats = statsObj;
                    const planToCheck = currentStats.nutritionPlan || currentStats.diet;
                    
                    if (!planToCheck || !planToCheck.comidas || planToCheck.comidas.length === 0) {
                        console.log("[GlobalWorker] Injecting emergency fallback plan globally...");
                        const { generateFallbackNutritionPlan } = await import('../geminiService');
                        const finalPlan = generateFallbackNutritionPlan({ ...currentStats, id: user.id });
                        currentStats.nutritionPlan = finalPlan;
                        
                        await supabase.from('profiles').update({
                            stats: { ...currentStats, needs_diet_reveal: true },
                            aura_diet_status: 'completed'
                        }).eq('id', user.id);
                        
                        const firstName = currentStats.name ? currentStats.name.split(' ')[0] : '';
                        const namePart = firstName ? ` ${firstName}` : '';
                        const isUpdate = currentStats.aura_generation_type === 'update';
                        const kcals = currentStats.daily_calories || 2000;
                        const prot = currentStats.daily_protein || 150;
                        
                        let message = isUpdate 
                           ? `¡Hecho${namePart}! Ya he aplicado los ajustes. Tienes todo listo basándome en tus requerimientos (${kcals} Kcal y ${prot}g de proteína). Échale un ojo a la pestaña de Dieta, seguimos a tope 🔥`
                           : `¡Listo${namePart}! Ya tienes tu plan nutricional listo. Cuadrando ${kcals} Kcal y ${prot}g de proteína diaria. Corre a la pestaña de Dieta a echarle un vistazo.`;
                        
                        const fallbackTargetTime = Date.now();
                        const finalMessage = `${message} [DELAYED:${fallbackTargetTime}] [MSG:DIET_READY]`;
                        
                        await supabase.from('chat_history').insert([{
                            user_id: user.id, role: 'model', content: finalMessage, created_at: new Date().toISOString(), seen: false
                        }]);
                    } else {
                        // Update existing plan
                        await supabase.from('profiles').update({
                            aura_diet_status: 'completed',
                            stats: { ...currentStats, needs_diet_reveal: true }
                        }).eq('id', user.id);
                        
                        const fallbackTargetTime = Date.now();
                        const finalMessage = `Ya he revisado todo y he ajustado tu plan actual para que sigas progresando. Tienes todo listo en la pestaña de Dieta. ¡A mutar! [DELAYED:${fallbackTargetTime}] [MSG:DIET_READY]`;
                        
                        await supabase.from('chat_history').insert([{
                            user_id: user.id, role: 'model', content: finalMessage, created_at: new Date().toISOString(), seen: false
                        }]);
                    }
                } catch (e) {
                    console.error("[GlobalWorker] Error generating global fallback:", e);
                } finally {
                    window.dispatchEvent(new Event('aura_diet_updated'));
                    setTimeout(() => { isExecutingFallbackRef.current = false; }, 10000); // 10s cooldown
                }
            };

            if (status !== prevStatus) {
                sessionStorage.setItem('aura_diet_prev_status', status);
            }

            // Global Target Tick (Fallback)
            // Even if we hit an error early, we wait for the target time to maintain the illusion.
            const isActivelyGenerating = status === 'processing' || status === 'generating_initial' || status === 'updating' || status === 'trigger_update' || status === 'error_generation' || status === 'error_finalization';
            
            let parsedCloudTarget = profileData.aura_target_completion_time || statsObj.aura_target_completion_time;
            if (typeof parsedCloudTarget === 'string') {
                const numTarget = Number(parsedCloudTarget);
                parsedCloudTarget = isNaN(numTarget) ? new Date(parsedCloudTarget).getTime() : numTarget;
            }
            
            const isTimerExpired = parsedCloudTarget && Date.now() >= parsedCloudTarget;
            const isFatalError = status === 'error_generation' || status === 'error_finalization';

            if (isActivelyGenerating && (isTimerExpired || isFatalError) && !isExecutingFallbackRef.current) {
                console.warn("[GlobalWorker] Timer expired while processing or fatal error detected. Forcing global fallback completion.");
                await executeFallback();
            }
        };

        const fetchStatus = async () => {
            if (user.id !== 'mock-user-id-123') {
                try {
                    const { data } = await supabase.from('profiles').select('*').eq('id', user.id).single();
                    if (data) {
                        handleJob(data);
                    }
                } catch (e) {
                    console.error("[GlobalWorker] Error fetching status on sync", e);
                }
            }
        };

        const forceCloudSync = () => fetchStatus();
        window.addEventListener('aura_force_cloud_sync', forceCloudSync);
        
        fetchStatus();

        const pollInterval = setInterval(() => {
            if (document.visibilityState === 'visible') {
                fetchStatus();
            }
        }, 5000);

        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                fetchStatus();
            }
        };
        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            clearInterval(pollInterval);
            window.removeEventListener('aura_force_cloud_sync', forceCloudSync);
        };
    }, [user ? user.id : null]);

    return null;
};
